import { useAtom } from 'jotai';
import { RESET } from 'jotai/utils';
import { useEffect, useState } from 'react';

import {
  type WorkspaceSwitchPayload,
  workspaceSwitchAtom,
} from '../../data/atoms/workspaceAtoms';
import { useWorkspaceId } from '../../data/query/useWorkspaceId';
import { useWorkspaceLoadSettled } from '../../data/query/useWorkspaceLoadSettled';
import { WorkspaceSwitchOverlay } from './workspace-switch-overlay';

/** The overlay shows for at least this long, so a switch reads as a change of context rather than a flicker. */
export const WORKSPACE_SWITCH_MIN_MS = 2_500;

/** The overlay never shows for longer than this, so a hung request cannot pin it. */
export const WORKSPACE_SWITCH_MAX_MS = 10_000;

/** Matches the overlay's `duration-300` exit fade. */
const EXIT_FADE_MS = 300;

/** True once `delayMs` has passed since mount. */
function useElapsed(delayMs: number): boolean {
  const [elapsed, setElapsed] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setElapsed(true), delayMs);
    return () => clearTimeout(timer);
  }, [delayMs]);
  return elapsed;
}

/**
 * Shows the switch overlay over the app while the document a workspace switch
 * reloaded into boots. A switch leaves `workspaceSwitchAtom` naming the target
 * (`switchWorkspace`) before it navigates; this captures the payload once, on
 * mount, and shows the overlay only when it names the workspace this document
 * booted with. Any other boot (a refresh, a cold open, a stale payload)
 * renders nothing.
 *
 * The atom is reset once mounted so the next boot in the tab is plain; the
 * captured copy keeps the overlay up through that write.
 */
export const WorkspaceSwitchLoader = () => {
  const [payload, setPayload] = useAtom(workspaceSwitchAtom);
  const [switched] = useState(payload);
  useEffect(() => setPayload(RESET), [setPayload]);
  const workspaceId = useWorkspaceId();
  return switched !== null && switched.workspaceId === workspaceId ? (
    <ActiveSwitch payload={switched} />
  ) : null;
};

/**
 * Every timer and subscription lives here, in a component that only exists
 * during a switch. The overlay leaves once the minimum time has passed and
 * the workspace's critical data has landed in this document, or at the cap;
 * then it fades and unmounts. Leaving is one-way.
 */
const ActiveSwitch = ({ payload }: { payload: WorkspaceSwitchPayload }) => {
  // Data counts only when fetched by this document: the persisted query cache
  // restores the previous workspace's entries with their old timestamps.
  const settled = useWorkspaceLoadSettled(performance.timeOrigin);
  const floorPassed = useElapsed(WORKSPACE_SWITCH_MIN_MS);
  const capPassed = useElapsed(WORKSPACE_SWITCH_MAX_MS);
  const shouldLeave = capPassed || (settled && floorPassed);

  const [phase, setPhase] = useState<'shown' | 'leaving' | 'done'>('shown');
  useEffect(() => {
    if (shouldLeave) setPhase((current) => (current === 'shown' ? 'leaving' : current));
  }, [shouldLeave]);
  useEffect(() => {
    if (phase !== 'leaving') return;
    const timer = setTimeout(() => setPhase('done'), EXIT_FADE_MS);
    return () => clearTimeout(timer);
  }, [phase]);

  if (phase === 'done') return null;
  return (
    <WorkspaceSwitchOverlay
      workspaceId={payload.workspaceId}
      workspaceName={payload.workspaceName}
      iconUrl={payload.iconUrl ?? null}
      isLeaving={phase === 'leaving'}
    />
  );
};
